import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { INVOICE_ATTACHABLE_DOCUMENTS } from '../../graphql';
import {
  getUniqueAttachableDocuments,
  getUploadDocumentType,
  isProviderManagedDocument,
} from '../../helpers';
import useGetInvoice from '../../hooks/useGetInvoice';
import useInvoiceDocumentUpload from '../../hooks/useInvoiceDocumentUpload';
import useIsStaff from '../../hooks/useIsStaff';
import { DETAILED_DATA_GRID_CLASS_NAME } from '../../theme/constants/data-grid';
import FingoDataGrid from '../dataGrids/FingoDataGrid';
import getInvoiceUploadedDocumentsColumns from '../../constants/invoice-uploaded-documents-columns';

const PROVIDER_DOCUMENT_TOOLTIP = 'Este documento se sincroniza automáticamente desde tu proveedor de facturación';

const InvoiceUploadedDocumentsTable = ({
  invoiceId,
  title,
  showTitle,
}) => {
  const invoice = useGetInvoice(invoiceId, INVOICE_ATTACHABLE_DOCUMENTS);
  const canReplaceProviderDocuments = useIsStaff();
  const hasInvoiceProvider = Boolean(invoice?.company?.hasInvoiceProviderCredentials);
  const {
    inputRef,
    documentToUpload,
    loading,
    handleOpenInput,
    handleChange,
    getUploadedUrlByType,
  } = useInvoiceDocumentUpload({ invoiceId, showSnackbar: true });

  const columns = getInvoiceUploadedDocumentsColumns({ handleOpenInput });

  const sourceRows = useMemo(() => getUniqueAttachableDocuments(invoice), [invoice]);

  const tableRows = useMemo(
    () => sourceRows.map((document) => {
      const uploadType = getUploadDocumentType(document);
      const isProviderLocked = isProviderManagedDocument(uploadType, hasInvoiceProvider)
        && !canReplaceProviderDocuments;
      return {
        ...document,
        uploadDocumentType: uploadType,
        documentUrl: document.url || getUploadedUrlByType(uploadType),
        disableUpload: !uploadType || isProviderLocked,
        uploadTooltip: isProviderLocked ? PROVIDER_DOCUMENT_TOOLTIP : '',
        isUploading: loading && documentToUpload?.documentType === uploadType,
      };
    }),
    [
      sourceRows,
      getUploadedUrlByType,
      loading,
      documentToUpload,
      hasInvoiceProvider,
      canReplaceProviderDocuments,
    ],
  );

  if (!tableRows.length) {
    return null;
  }

  return (
    <Stack direction="column" gap="8px">
      {showTitle && (
        <Typography variant="small_3" fontWeight={600} sx={{ textAlign: 'center' }}>
          {title}
        </Typography>
      )}
      <FingoDataGrid
        columns={columns}
        rows={tableRows}
        dataGridClassName={DETAILED_DATA_GRID_CLASS_NAME}
        serverFilters={false}
        rowHeight={48}
        hideFooter
        fitTableToContent
        disableColumnMenu
        disableColumnSort
        getRowClassName={() => ''}
      />
      <input
        type="file"
        ref={inputRef}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </Stack>
  );
};

InvoiceUploadedDocumentsTable.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  title: PropTypes.string,
  showTitle: PropTypes.bool,
};

InvoiceUploadedDocumentsTable.defaultProps = {
  title: 'Documentos cargados',
  showTitle: true,
};

export default InvoiceUploadedDocumentsTable;
